import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrainTrainingGameIcon, FLANKER_ICON_BY_DIRECTION, GameShell, randomItem } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy, getDirectionLabel } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;

const ROUTE_DIRECTIONS = [
    { dc: -1, dr: 0, id: 'left' },
    { dc: 1, dr: 0, id: 'right' },
    { dc: 0, dr: -1, id: 'up' },
    { dc: 0, dr: 1, id: 'down' },
];

const createRouteRound = (level = 1) => {
    const size = 5;
    const start = { col: 2, row: 2 };
    const length = Math.min(8, 2 + level);
    const steps = [];
    let position = { ...start };
    let previous = '';

    for (let index = 0; index < length; index += 1) {
        const valid = ROUTE_DIRECTIONS.filter((direction) => {
            const nextCol = position.col + direction.dc;
            const nextRow = position.row + direction.dr;
            const reverse = {
                down: 'up',
                left: 'right',
                right: 'left',
                up: 'down',
            }[previous];
            return nextCol >= 0 && nextCol < size && nextRow >= 0 && nextRow < size && direction.id !== reverse;
        });
        const direction = randomItem(valid.length ? valid : ROUTE_DIRECTIONS.filter((item) => {
            const nextCol = position.col + item.dc;
            const nextRow = position.row + item.dr;
            return nextCol >= 0 && nextCol < size && nextRow >= 0 && nextRow < size;
        }));
        steps.push(direction.id);
        position = { col: position.col + direction.dc, row: position.row + direction.dr };
        previous = direction.id;
    }

    return {
        answerIndex: position.row * size + position.col,
        id: `${Date.now()}-${Math.random()}`,
        size,
        startIndex: start.row * size + start.col,
        steps,
    };
};

function CompassRouteGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('compass-route', initialLevel);
    const timersRef = useRef([]);
    const [round, setRound] = useState(() => createRouteRound(startingLevel));
    const [phase, setPhase] = useState('start');
    const [stepIndex, setStepIndex] = useState(0);
    const [choice, setChoice] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = choice !== null && choice === round.answerIndex;

    const clearTimers = useCallback(() => {
        timersRef.current.forEach((timer) => window.clearTimeout(timer));
        timersRef.current = [];
    }, []);

    const playRound = useCallback((nextRound) => {
        clearTimers();
        setRound(nextRound);
        setPhase('start');
        setStepIndex(0);
        setChoice(null);

        timersRef.current.push(window.setTimeout(() => {
            setPhase('steps');
            nextRound.steps.forEach((step, index) => {
                timersRef.current.push(window.setTimeout(() => setStepIndex(index), index * 560));
            });
            timersRef.current.push(window.setTimeout(
                () => setPhase('answer'),
                nextRound.steps.length * 560 + 180
            ));
        }, 850));
    }, [clearTimers]);

    useEffect(() => {
        playRound(round);
        return clearTimers;
    }, []);

    const chooseCell = (index) => {
        if (phase !== 'answer' || choice !== null) {
            return;
        }

        const isCorrect = index === round.answerIndex;
        const nextStreak = isCorrect ? streak + 1 : 0;
        const nextLevel = normalizeBrainGameLevel('compass-route', startingLevel + Math.floor(nextStreak / 2));
        setChoice(index);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setPhase('feedback');
        timersRef.current.push(window.setTimeout(
            () => playRound(createRouteRound(nextLevel)),
            isCorrect ? 560 : 820
        ));
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{
                level: normalizeBrainGameLevel('compass-route', startingLevel + Math.floor(streak / 2)),
                score,
                streak,
            }}
            tone={correct ? 'good' : choice !== null ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__route-game">
                <div
                    className="UFOV-brain-games__transient-slot is-route-cue"
                    aria-hidden={phase !== 'steps'}
                >
                    {phase === 'steps' ? (
                        <div
                            className="UFOV-brain-games__route-arrow"
                            aria-label={formatGameCopy(copy.routeDirections, {
                                directions: round.steps
                                    .slice(0, stepIndex + 1)
                                    .map((step) => getDirectionLabel(copy, step))
                                    .join(', '),
                            })}
                        >
                            {round.steps.slice(0, stepIndex + 1).map((step, index) => (
                                <span className="UFOV-brain-games__route-step" key={`${round.id}-${index}`}>
                                    <BrainTrainingGameIcon icon={FLANKER_ICON_BY_DIRECTION[step]} />
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.compassRouteCue}</div>
                <div className="UFOV-brain-games__route-grid" aria-label={copy.compassRouteCue}>
                    {Array.from({ length: round.size * round.size }, (_, index) => (
                        <button
                            aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                            className={`${phase === 'start' && index === round.startIndex ? 'is-start' : ''} ${choice === index ? (index === round.answerIndex ? 'is-correct' : 'is-wrong') : ''} ${choice !== null && index === round.answerIndex ? 'is-answer' : ''}`.trim()}
                            disabled={phase !== 'answer'}
                            key={index}
                            onClick={() => chooseCell(index)}
                            type="button"
                        />
                    ))}
                </div>
            </div>
        </GameShell>
    );
}

export default CompassRouteGame;
